import _ from 'lodash';
import { types } from '@/api/amplify/graphql/types/widgets';
import { Logger } from '@/helpers/log';

const logger = new Logger('api:legacy:migration:widgets');

const nestedKey = (object, key) => {
  const value = object[key];
  if (value && typeof value === 'object') {
    logger.warn('chasing nested key', object, key);
    return nestedKey(value, key);
  }
  return value;
};

function unlinkFilter(filter) {
  if (filter === undefined || filter === null) {
    return undefined;
  }
  let ref = filter;
  if (typeof filter === 'object') {
    const $id = nestedKey(filter, '$id');
    const id = nestedKey(filter, 'id');
    ref = $id || id;
  }
  if (!ref || typeof ref !== 'string') {
    logger.warn('could not unlink filter/ref', filter, ref);
    return undefined;
  }
  if (ref.toLowerCase() === 'all') {
    return undefined;
  }
  return ref.replace('/savedSearches/', '');
}
const isGuid = (ref) => ref
  .match(/^(\{){0,1}[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}(\}){0,1}$/);
const isPropertyRef = (ref) => (
  ref !== null && (ref.startsWith('/properties/') || ref === '$id' || isGuid(ref))
);
function unlinkProperty(property) {
  if (property === undefined || property === null) {
    return undefined;
  }
  let ref = property;
  if (typeof property === 'object') {
    const $id = nestedKey(property, '$id');
    const id = nestedKey(property, 'id');
    const name = nestedKey(property, 'name');
    if (!name.startsWith('$calc') && name.startsWith('$')) {
      // $ properties do not have ids.
      return name;
    }
    ref = $id || id;
    if (!ref) {
      logger.warn('could not ref property by id', property);
      ref = name;
    }
  } else if (!isPropertyRef(property)) {
    logger.warn('could not verify property ref as id', property);
  }
  return ref.replace('/properties/', '');
}
function cloneIfObjects(...args) {
  return args
    .map((o) => {
      if (!o || typeof o !== 'object') {
        return undefined;
      }
      let clone = _.cloneDeep(o);
      if (typeof clone.$savedSearch === 'object') {
        logger.warn('preferring incident id to saved search for', o);
        clone = { ...clone.$savedSearch, $id: clone.$id };
      }
      const $id = nestedKey(clone, '$id');
      const name = nestedKey(clone, 'name');
      if (!$id) {
        if (!name || !name.startsWith('$')) {
          logger.warn('attempted to resource object without $id', o);
        }
        return undefined;
      }
      if ($id.toLowerCase() === 'all') {
        return undefined;
      }
      clone.$id = $id
        .replace('/savedSearches/', '')
        .replace('/properties/', '');
      return clone;
    })
    .filter((o) => o);
}

function fromAggregate(settings) {
  const { property, filter, type } = settings;
  const data = {
    ...settings,
    property: unlinkProperty(property),
    filter: unlinkFilter(filter),
    chartType: type,
  };
  const resources = {
    properties: cloneIfObjects(property),
    filters: cloneIfObjects(filter),
    kpis: [],
  };
  return { data, resources };
}
function fromEvents(settings) {
  const { incidentConfig, scopeConfig } = settings;
  const data = {
    ...settings,
    incidentConfig: unlinkFilter(incidentConfig),
    scopeConfig: unlinkFilter(scopeConfig),
  };
  const resources = {
    properties: [],
    filters: cloneIfObjects(incidentConfig, scopeConfig),
    kpis: [],
  };
  return { data, resources };
}
function fromList(settings) {
  const { filter, state = {} } = settings;
  const data = {
    ...settings,
    filter: unlinkFilter(filter),
    state: {
      ...state,
      columns: (state.columns || []).filter(({ visible }) => visible),
    },
  };
  const resources = {
    properties: [],
    filters: cloneIfObjects(filter),
    kpis: [],
  };
  return { data, resources };
}
function fromMap(settings) {
  const {
    filter,
    colouring,
    tooltipProperty,
    tooltipProperties,
  } = settings;
  const colouringRef = _.cloneDeep(colouring || {});
  if (colouring) {
    colouring.property = unlinkProperty(colouring.property);
  }

  let tooltipArray = [];
  if (tooltipProperties && Array.isArray(tooltipProperties)) {
    tooltipArray = tooltipProperties.map((p) => unlinkProperty(p)).filter((i) => i);
  }

  const data = {
    ...settings,
    filter: unlinkFilter(filter),
    tooltipProperty: unlinkProperty(tooltipProperty),
    tooltipProperties: tooltipArray,
    colouring,
  };
  const resources = {
    properties: cloneIfObjects(colouringRef.property, tooltipProperty),
    filters: cloneIfObjects(filter),
    kpis: [],
  };
  return { data, resources };
}
function fromProperty(settings) {
  const { property, scopeSearch } = settings;
  const data = {
    ...settings,
    property: unlinkProperty(property),
    scopeSearch: unlinkFilter(scopeSearch),
  };
  const resources = {
    properties: cloneIfObjects(property),
    filters: cloneIfObjects(scopeSearch),
    kpis: [],
  };
  return { data, resources };
}
function fromStatus(settings) {
  const { testSearch, scopeSearch } = settings;
  const data = {
    ...settings,
    testSearch: unlinkFilter(testSearch),
    scopeSearch: unlinkFilter(scopeSearch),
  };
  const resources = {
    properties: [],
    filters: cloneIfObjects(testSearch, scopeSearch),
    kpis: [],
  };
  return { data, resources };
}
function fromCohort(settings) {
  const { kpi } = settings;
  const data = {
    ...settings,
    kpi: unlinkFilter(kpi),
  };
  const resources = {
    properties: [],
    filters: [],
    kpis: cloneIfObjects((kpi || {}).name ? kpi : null),
  };
  return { data, resources };
}
function fromFloorplan(settings = {}) {
  const {
    filter, xProperty, yProperty, tooltipProperty,
  } = settings;
  const data = {
    ...settings,
    filter: unlinkFilter(filter),
    xProperty: unlinkProperty(xProperty),
    yProperty: unlinkProperty(yProperty),
    tooltipProperty: unlinkProperty(tooltipProperty),
  };
  const resources = {
    properties: cloneIfObjects(xProperty, yProperty),
    filters: cloneIfObjects(filter),
    kpis: [],
  };
  return { data, resources };
}
function fromDevice(settings = {}) {
  const { displayedProperties } = settings;
  const data = {
    ...settings,
    displayedProperties: (displayedProperties || []).map((p) => unlinkProperty(p)).filter((i) => i),
  };
  const resources = {
    properties: cloneIfObjects(...displayedProperties),
    filters: [],
    kpis: [],
  };
  return { data, resources };
}
function fromWorkflow(settings = {}) {
  const { workflowProperty } = settings;
  const data = {
    ...settings,
    workflowProperty: unlinkProperty(workflowProperty),
  };
  const resources = {
    properties: cloneIfObjects(workflowProperty),
    filters: [],
    kpis: [],
  };
  return { data, resources };
}
function fromAlarms(settings = {}) {
  const data = {
    ...settings,
  };
  const resources = {
    properties: [],
    filters: [],
    kpis: [],
  };
  return { data, resources };
}
const froms = {
  aggregate: fromAggregate,
  'events-chart': fromEvents,
  list: fromList,
  map: fromMap,
  property: fromProperty,
  status: fromStatus,
  cohort: fromCohort,
  floorplan: fromFloorplan,
  device: fromDevice,
  workflow: fromWorkflow,
  alarms: fromAlarms,
};
const typeMap = {
  aggregate: types.aggregate,
  'events-chart': types.event,
  list: types.list,
  map: types.map,
  property: types.property,
  status: types.status,
  cohort: types.cohort,
  floorplan: types.floorplan,
  device: types.device,
  alarms: types.alarms,
  workflow: types.workflow,
};

export function fromLegacy(widget) {
  const { id, type, settings } = widget;
  logger.debug('converting', id, type, settings);
  const { resources, data } = froms[type](settings);
  return {
    widget: { ...data, id, type: typeMap[type] },
    resources,
  };
}

function toAggregate(data) {
  const { chartType, ...settings } = data;
  return {
    ...settings,
    type: chartType,
  };
}
function toAny(data) {
  const widgetData = { ...data };
  if (widgetData.scope) {
    delete widgetData.scope;
  }
  return widgetData;
}
const tos = {
  [types.aggregate]: toAggregate,
  [types.event]: toAny,
  [types.list]: toAny,
  [types.map]: toAny,
  [types.property]: toAny,
  [types.status]: toAny,
  [types.cohort]: toAny,
  [types.floorplan]: toAny,
  [types.device]: toAny,
  [types.workflow]: toAny,
  [types.alarms]: toAny,
};

export function toLegacy(widget) {
  const { id, type, ...data } = widget;
  const settings = tos[type](data);
  const [legacyType] = Object
    .entries(typeMap)
    .find(([__, v]) => v === type); // eslint-disable-line no-unused-vars
  return { id, type: legacyType, settings };
}
